import React, { Children } from 'react';
import { TargetIcon, ShieldCheckIcon, TrendingUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function SimpleEngineOverview() {
  const systems = [{
    icon: TargetIcon,
    title: 'The Recruiting Engine',
    tagline: 'Predictable applicant flow',
    description: 'Stop gambling on job boards. We help you install a cold-market sourcing system designed to generate consistent, qualified applicants each month.',
    benefits: ['Targeted, role-specific messaging', 'Campaign strategy and positioning guidance', 'Automated screening and qualification support', 'Predictable monthly interview volume']
  }, {
    icon: ShieldCheckIcon,
    title: 'The Retention Engine',
    tagline: 'Turn hires into launches',
    description: 'Most reps quit in the first 8 weeks. We help build the structure, communication, and accountability that get rookies to their first wins and beyond.',
    benefits: ['Structured onboarding pathway', 'Early-stage engagement checkpoints', 'Support systems for mindset, expectations, and performance', 'Increased Week 1–8 resilience and retention'],
    disclaimer: '(Note: Results vary by company and leadership engagement.)'
  }, {
    icon: TrendingUpIcon,
    title: 'The Results Engine',
    tagline: 'Rookies to producers',
    description: 'Launch is only the beginning. The system supports leadership with training frameworks and live weekly sales schools that accelerate development and increase long-term value.',
    benefits: ['Skill and behavioral reinforcement', 'Performance tracking guidance', 'Role clarity and development planning', 'Scalable framework for team consistency']
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section className="section-padding bg-gray-50" data-id="element-941">
      <div className="container-custom" data-id="element-942">
        <motion.div className="max-w-3xl mx-auto text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} data-id="element-943">
          <h2 className="mb-6" data-id="element-944">The Three-Part HIRE System</h2>
          <p className="text-xl text-gray-600 leading-relaxed" data-id="element-945">
            Each part of the system is designed to solve a specific problem in
            the recruiting and early-retention cycle. Together, they create
            consistency, stability, and predictable growth.
          </p>
        </motion.div>

        <motion.div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-946">
          {systems.map((system, index) => {
          const Icon = system.icon;
          return <motion.div key={index} className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-slate transition-all" variants={itemVariants} whileHover={{
            scale: 1.02,
            transition: {
              duration: 0.2
            }
          }} data-id="element-947">
                <div className="w-16 h-16 bg-gradient-slate rounded-xl flex items-center justify-center mb-6" data-id="element-948">
                  <Icon className="h-8 w-8 text-white" data-id="element-949" />
                </div>
                <h3 className="text-2xl mb-2" data-id="element-950">{system.title}</h3>
                <p className="text-slate font-semibold mb-4" data-id="element-951">
                  {system.tagline}
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-952">
                  {system.description}
                </p>
                <div className="mb-4" data-id="element-953">
                  <p className="text-sm font-semibold text-deep-blue mb-3" data-id="element-954">
                    Includes:
                  </p>
                  <ul className="space-y-3" data-id="element-955">
                    {system.benefits.map((benefit, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-956">
                        <span className="text-slate mr-2 flex-shrink-0 font-bold" data-id="element-957">
                          ✓
                        </span>
                        {benefit}
                      </li>)}
                  </ul>
                </div>
                {system.disclaimer && <p className="text-xs text-gray-500 italic mt-4" data-id="element-958">
                    {system.disclaimer}
                  </p>}
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}